import { call, put, select } from 'redux-saga/effects';

import {
  updateCheckoutErrorStatus,
  updateExistingUserEmail,
  updateLoadingUser,
  updateMatch,
  updateUserAndPatient,
} from 'connect/actions';
import {
  CHECKOUT_ERROR_TYPES,
  getUserSignupErrorType,
} from 'connect/constants';
import { extractErrorMessage, sendToLogger } from 'connect/saga/helpers';
import { getUserAndPatient } from 'connect/saga/saga';
import { selectDataMatch, selectDataMatchToken, selectDataPatient, selectDataUserAuthenticated } from 'connect/selectors';
import {
  apiSignupUserAsPatient,
} from 'endpoints/patient-endpoints';

import { UpdatePatientDetails } from '../patient/update-details';
import { SagaHandler } from '../SagaHandler';
import { UpsertIterableUser } from '../upsert-iterable-user/upsert-iterable-user';

import { PRETTY_SIGNUP_ERROR_MESSAGES } from './errorMessageConstants';

class _Signup extends SagaHandler {
  constructor() {
    super({
      actionName: 'data/SIGNUP_USER',
      stateKey: 'signup',
    });
  }

  createAction(userData, match, matchToken) {
    return {
      type: this._actionName,
      userData,
      match,
      matchToken,
    };
  }

  getPrettyErrorMessage(errorType) {
    if (!errorType) {
      return null;
    }

    const prettyErrorMessage = PRETTY_SIGNUP_ERROR_MESSAGES[errorType];

    return prettyErrorMessage ?? 'An unknown error occurred';
  }

  * saga({ userData, match, matchToken }) {
    const errorHandler = this.error;
    const loadingHandler = this.loading;
    yield put(loadingHandler.update(true));
    yield put(updateLoadingUser(true));

    const currentMatch = match || (yield select(selectDataMatch));
    const currentMatchToken = matchToken || (yield select(selectDataMatchToken));
    yield put(errorHandler.update(null));
    const patientData = {};
    if (currentMatch) {
      patientData.metadata = userData?.metadata || {};
      patientData.metadata.wizardData = { ...currentMatch.other };
      patientData.metadata.wizardData.location = currentMatch.location;
    }

    let user;
    try {
      user = yield call(getUserAndPatient);
      // If user is already authenticated, we don't need to sign them up again
      const isUserAuthenticated = yield select(selectDataUserAuthenticated);
      if (!isUserAuthenticated) {
        user = yield call(apiSignupUserAsPatient, userData);

        // Use the populated user to fetch the patient data
        const populatedUser = yield call(getUserAndPatient);
        yield put(updateUserAndPatient(populatedUser));
      }

      // Add ownership of match to authenticated user
      if (currentMatch?.id && currentMatchToken) {
        yield call(updateMatch, {
          matchId: currentMatch.id,
          currentMatchToken,
        });
      }

      const patient = yield select(selectDataPatient);

      const isLoggedIn = Boolean(user?.id && patient?.id);
      const hasPatientMetadata = Object.keys(patientData.metadata || {}).length;

      // If anything in patientData, make additional API call
      if (isLoggedIn && hasPatientMetadata) {
        yield put(UpdatePatientDetails.createAction({ patientDetails: patientData, autoVerify: true }));
      }
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      const errorType = getUserSignupErrorType(errorMessage);
      yield put(errorHandler.update(errorType));

      // If signing user up failed and they have a match, it happened in the checkout flow
      // Otherwise, it happened in the funnel, and we don't want to affect this
      if (currentMatch) {
        if (errorType) {
          yield put(updateCheckoutErrorStatus(errorType));

          if (errorType === CHECKOUT_ERROR_TYPES.EMAIL_ALREADY_REGISTERED) {
            yield put(updateExistingUserEmail());
          }
        } else {
          yield put(updateCheckoutErrorStatus(null));
        }
      }

      sendToLogger(error, 'SignupUser');
    } finally {
      yield put(updateLoadingUser(false));
      yield put(loadingHandler.update(false));
    }

    // Separate try/catch block for Iterable user creation, after everything else is done, as this should never interfere with user signup in any way
    try {
      if (!user?.email) {
        return;
      }

      const { other } = currentMatch || {};

      const dataFields = {
        signedUpInCheckout: Boolean(currentMatch),
        questionReason: other?.reason,
        questionSubReasons: other?.subReasons?.join(', '),
        selectedTreatments: other?.selectedTreatments
          ?.map((treatment) => treatment.title)
          ?.join(', '),
      };

      yield call(UpsertIterableUser.saga, {
        payload: {
          dataFields,
        },
      });
    } catch (error) {
      sendToLogger(error, 'upsertIterableUser');
    }
  }
}

export const Signup = new _Signup();
